import { Component, OnInit } from '@angular/core';
import { MessageBoxComponent } from '@common/popups/message-box/message-box.component';
import { LayoutService } from '@common/services/layout.service';
import { ModalService } from '@common/services/modal.service';
import { CalendarComponent } from '@common/UI/calendar/calendar.component';
import { NumberInputComponent } from '@common/UI/number-input/number-input.component';
import { SortableExtractor } from '@common/UI/sortable/sortable.component';
import { Filterable } from '@common/utilities/Filterable';
import { Lookup } from '@common/utilities/Lookup';
import { main } from '@core/operators';
import { ConfigsService } from '@core/services/configs.service';
import { DataService, GoodRecord, InvoiceCategory, InvoiceDetailRecord, InvoiceRecord, InvoiceState, invoiceState, InvoiceType, PartnerRecord, parts2SKU, ProductUnit, SKU2Parts, UserRecord } from '@core/services/data.service';
import { PartnersService } from '@core/services/partners.service';
import { SynchronizationService } from '@core/services/synchronization.service';
import { TagsService } from '@core/services/tags.service';
import { UsersService } from '@core/services/users.service';
import { Multilingual } from '@core/utilities/multilingual';
import { TextEditorComponent } from '@routes/editors/text-editor/text-editor.component';
import { ViewerComponent } from '@routes/invoices/viewer/viewer.component';
import { map } from 'rxjs/operators';
import { DatePipe } from '@angular/common';

interface DetailGroup {
  ImportInvoiceTag: string;
  SKU: string;
  Color: string;
  Unit: ProductUnit
  records: InvoiceDetailRecord[];
}

interface DetailExtractor<T> {

  count: (item: T) => number;
  actualQuantity: (item: T) => number;
  quoteQuantity: (item: T) => number;
  quoteUnitPrice: (item: T) => number;
  quoteIcon: (item: T) => string;
  subtotal: (item: T) => number;

  sum: (field: string, unit?: ProductUnit) => number;
}

interface InvoiceGroup {

  partner: PartnerRecord;

  EditableCount: number;
  ConfirmedCount: number;
  LengthCount: number;
  WeightCount: number;
  LengthActualQuantity: number;
  WeightActualQuantity: number;
  ConfirmedStamp: number;
  EditableStamp: number;
  Total: number;
  invoices: InvoiceRecord[];
}

@Component({
  templateUrl: './exports.component.html',
  styleUrls: ['./exports.component.scss']
})
export class ExportsComponent implements OnInit {

  public importData() {

    this.modal.show(TextEditorComponent, { state: { title: 'Xuất hàng bằng dữ liệu excel', text: '[TÊN CÔNG | X] [MÃ HÀNG] [MÃ MÀU] [SỐ CÂY] [DÀI 1] [DÀI 2] [DÀI 3] [DÀI 4] [DÀI 5] [DÀI 6] [DÀI TÍNH TIỀN] [GIÁ] [THÀNH TIỀN]' } }).content.commit.subscribe(text => {

      let lines = text.split('\n');

      let tag: string = '';
      let sku: string = '';
      let color: string = '';
      let batch: { sku: string, color: string, length: number, price: number }[] = [];
      let records: { [key: string]: { sku: string, color: string, length: number, price: number }[] } = {};

      for (let line of lines) {

        let cols = line.split('\t');

        if (cols.length < 5) continue;

        if (cols[0] != '') tag = cols[0].toUpperCase();

        if (cols[1] != '') {
          let parts = SKU2Parts(cols[1].replace(/(#)/i, '').toUpperCase());
          sku = parts2SKU(parts[0], parts[1]);
        }

        if (cols[2] != '') color = cols[2].split(/(-|~|号)/i)[0].replace(/(#)/i, '').toUpperCase();

        if (tag != '' && sku != '' && color != '') {

          if (!records[tag]) records[tag] = [];

          for (let i = 4; i < 10; i++) {

            if (i >= cols.length) break;

            let length: number = parseFloat(cols[i]);

            if (!isNaN(length)) {

              let record = { sku: sku, color: color, length: length, price: 0 };

              records[tag].push(record);
              batch.push(record);
            }
          }
        }

        if (cols[11] != '') {
          let price = parseFloat(cols[11].replace(',',''));

          if (!isNaN(price) && price > 0) {
            batch.forEach(i => i.price = price);
            batch = [];
          }
        }
      }

      for (let invoiceTag in records) {

        if (invoiceTag != 'X') {

          let data = { Tag: invoiceTag };

          this.sync.with(ExportsComponent).commit(done => this.ds.invoices('get', data).subscribe(invoices => {

            if (invoices && invoices.length == 1 && invoices[0].Type == InvoiceType.Import) {

              records[invoices[0].Tag].forEach(good => {

                let original = invoices[0].Details.first(i => i.SKU == good.sku && i.Color == good.color && i.ActualQuantity == good.length && i.CustomerID == 0);

                if (original) {

                  if (good.price == 0 && invoices[0].PartnerID != 1) good.price = original.QuoteUnitPrice;

                  let candidate = this.goods.source.first(i => i.ImportInvoiceTag == original.ImportInvoiceTag && i.SKU === good.sku && i.Color === good.color && i.ActualQuantity === good.length);
                  if (candidate) this.add(candidate, good.price);

                  invoices[0].Details.remove(original);
                }
              });
            }

            done();
          }));
        }
      }

      if (records['X']) {

        this.sync.when(ExportsComponent).commit(() => {
          records['X'].forEach(good => {
            let candidate = this.goods.source.first(i => i.SKU === good.sku && i.Color === good.color && i.ActualQuantity === good.length);
            if (candidate) this.add(candidate, good.price);
          })
        });
      }
    });
  }

  constructor(
    private layout: LayoutService,
    private ds: DataService,
    private modal: ModalService,
    private sync: SynchronizationService,
    private _partners: PartnersService,
    private _users: UsersService,
    private _tags: TagsService,
    public configs: ConfigsService,
    private date: DatePipe
  ) {
  }
  public get loading(): boolean {
    return !this.sync.ready(ExportsComponent, PartnersService, TagsService);
  }

  public invoices = new Filterable<InvoiceRecord>({
    extractor: i => [i.Tag],
    grouper: i => [
      { name: 'Trạng thái', option: this.state(i) },
    ]
  });

  public partners = new Filterable<InvoiceGroup>({
    extractor: i => [i.partner.Name, 'K' + i.partner.ID],
    grouper: i => [
      { name: 'Đơn xong', option: i.ConfirmedCount > 0 ? 'Có đơn' : 'Không có' },
      { name: 'Đơn chờ', option: i.EditableCount > 0 ? 'Có đơn' : 'Không có' },
      { name: 'Loại hàng xuất', option: i.LengthCount > 0 ? (i.WeightCount > 0 ? 'Xuất hàng hỗn hợp' : 'Chỉ xuất hàng mét') : i.WeightCount > 0 ? 'Chỉ xuất hàng cân' : 'Không có hàng xuất' },
    ]
  });

  public all: InvoiceGroup = {
    partner: { ID: 0, Name: Multilingual.getText('Tất cả đơn hàng'), Address: '', Phone: '', Email: '', DealerID: 0, ImportPolicyID: 0, ExportPolicyID: 0 },
    invoices: [],
    ConfirmedCount: 0,
    ConfirmedStamp: 0,
    EditableCount: 0,
    EditableStamp: 0,
    LengthCount: 0,
    LengthActualQuantity: 0,
    WeightCount: 0,
    WeightActualQuantity: 0,
    Total: 0
  };

  public goods = new Filterable<GoodRecord>({
    extractor: i => [i.SKU + "m" + i.Color + "d" + i.ActualQuantity, i.SKU + "d" + i.ActualQuantity],
    grouper: i => [{ name: 'Nguồn nhập', option: i.ImportInvoiceTag, description: this.source(i.ImportInvoiceTag) }]
  });

  private goodLU = new Lookup<GoodRecord>(i => i.ID);

  public selected: { invoice: InvoiceRecord, partner: PartnerRecord, details: Filterable<DetailGroup> } = { invoice: null, partner: null, details: new Filterable<DetailGroup>() };

  private edited: boolean = false;

  public editPrice(group: DetailGroup, input: NumberInputComponent) {
    if (isNaN(input.value)) input.value = 0;
    group.records.forEach(i => i.QuoteUnitPrice = input.value);
    this.edited = true;
  }

  public editStamp() {

    if (!this.editable) return;

    let ref = this.modal.show(CalendarComponent, { state: { selected: new Date(this.selected.invoice.Stamp) } });
    ref.content.change.subscribe(value => {
      this.selected.invoice.Stamp = value.getTime();
      ref.hide();
      this.edited = true;
    });
  }


  public create() {

    if (this.selected.partner.ID < 1) return;

    this.open({
      ID: 0,
      Allocations: [],
      Category: InvoiceCategory.Qualified,
      Symbol: 'VND',
      Icon: 'đ',
      CreatorID: this._users.current.ID,
      UpdatorID: this._users.current.ID,
      Stamp: new Date().getTime(),
      FirstStamp: new Date().getTime(),
      LastStamp: new Date().getTime(),
      PartnerID: this.selected.partner.ID,
      Type: InvoiceType.Export,
      State: InvoiceState.Editable,
      Tag: '',
      Total: 0,
      Details: []
    });

    this.editStamp();
  }

  public add(good: GoodRecord, price: number = 0) {

    if (price == 0) price = good.ExportUnitPrice;

    this.selected.invoice.Details.push({
      ID: good.ID,
      SKU: good.SKU,
      Color: good.Color,
      Unit: good.Unit,
      ActualQuantity: good.ActualQuantity,
      QuoteQuantity: good.ExportQuoteQuantity,
      CustomerID: this.selected.partner.ID,
      ExportOrderID: good.ExportOrderID,
      ImportOrderID: good.ImportOrderID,
      ExportOrderTag: '',
      ImportOrderTag: '',
      ImportInvoiceTag: good.ImportInvoiceTag,
      IsPriceEditable: true,
      QuoteUnitPrice: price,
      VendorID: good.VendorID
    });

    let index = this.goods.pageIndex;
    this.goods.source = this.goods.source.filter(i => i != good).order(i => [i.ImportInvoiceTag, i.SKU, i.Color, i.Unit, i.ActualQuantity].join('/'));
    this.goods.pageIndex = index;


    this.selected.details.source = this.selected.invoice.Details.group(i => i.ImportInvoiceTag + '/' + i.SKU + '/' + i.Color + '/' + i.Unit + '/' + i.QuoteUnitPrice + '/' + i.IsPriceEditable).map(i => ({ ImportInvoiceTag: i.items[0].ImportInvoiceTag, SKU: i.items[0].SKU, Color: i.items[0].Color, Unit: i.items[0].Unit, Reduced: i.items.sum(x => x.ActualQuantity - x.QuoteQuantity), records: i.items }));
    this.edited = true;
  }

  public remove(detail: InvoiceDetailRecord) {

    this.selected.invoice.Details.remove(detail);
    this.goods.source = this.goods.source.union([this.goodLU.get(detail.ID)]).order(i => [i.ImportInvoiceTag, i.SKU, i.Color, i.Unit, i.ActualQuantity].join('/'));
    this.selected.details.source = this.selected.invoice.Details.group(i => i.ImportInvoiceTag + '/' + i.SKU + '/' + i.Color + '/' + i.Unit + '/' + i.QuoteUnitPrice + '/' + i.IsPriceEditable).map(i => ({ ImportInvoiceTag: i.items[0].ImportInvoiceTag, SKU: i.items[0].SKU, Color: i.items[0].Color, Unit: i.items[0].Unit, Reduced: i.items.sum(x => x.ActualQuantity - x.QuoteQuantity), records: i.items }));
    this.edited = true;
  }

  public update() {
    this.sync.with(ExportsComponent).commit(done => {
      this.ds.invoices(this.selected.invoice.ID > 0 ? 'update' : 'create', this.selected.invoice).subscribe(invoices => {
        if (invoices && invoices.length == 1) {

          done();
          this.synchronize();

          this.sync.when(ExportsComponent).commit(() => {

            this.open(invoices[0]);
            this.edited = false;
          });
        }
        else {
          done();
        }
      });
    });
  }

  public synchronize() {

    let invoices: InvoiceRecord[];

    this.sync.with(ExportsComponent).repeat(
      () => this.ds.goods('remaining').pipe(map(data => {
        if (data) {
          this.goodLU.source = data;
          return true;
        }
        return false;
      })),

      () => this.ds.invoices("exports").pipe(map(data => {
        if (data) {
          invoices = data.filter(i => i.PartnerID > 2);
          return true;
        }
        return false;
      }))
    );

    this.sync.when(PartnersService, ExportsComponent).commit(() => {

      this.all = {
        ...this.all,
        invoices: invoices,

        LengthCount: invoices.sum(i => i.LengthCount),
        LengthActualQuantity: invoices.sum(i => i.LengthActualQuantity),

        WeightCount: invoices.sum(i => i.WeightCount),
        WeightActualQuantity: invoices.sum(i => i.WeightActualQuantity),

        ConfirmedCount: invoices.count(i => i.State == InvoiceState.Confirmed),
        ConfirmedStamp: (invoices as any[]).union([{ State: InvoiceState.Confirmed, Stamp: 0 }]).max(i => i.State == InvoiceState.Confirmed ? i.Stamp : 0),

        EditableCount: invoices.count(i => i.State == InvoiceState.Editable),
        EditableStamp: (invoices as any[]).union([{ State: InvoiceState.Editable, Stamp: 0 }]).max(i => i.State == InvoiceState.Editable ? i.Stamp : 0),
        Total: invoices.sum(i => i.Total) + 0.08 * invoices.sum(i => i.Total)
      };

      this.partners.source = this._partners.all.filter(i => i.ID > 3).map<InvoiceGroup>(partner => {

          let matches = invoices.filter(i => i.PartnerID == partner.ID);

          return {
            partner: partner,
            invoices: matches,

            LengthCount: matches.sum(i => i.LengthCount),
            LengthActualQuantity: matches.sum(i => i.LengthActualQuantity),

            WeightCount: matches.sum(i => i.WeightCount),
            WeightActualQuantity: matches.sum(i => i.WeightActualQuantity),

            ConfirmedCount: matches.count(i => i.State == InvoiceState.Confirmed),
            ConfirmedStamp: (matches as any[]).union([{ State: InvoiceState.Confirmed, Stamp: 0 }]).max(i => i.State == InvoiceState.Confirmed ? i.Stamp : 0),

            EditableCount: matches.count(i => i.State == InvoiceState.Editable),
            EditableStamp: (matches as any[]).union([{ State: InvoiceState.Editable, Stamp: 0 }]).max(i => i.State == InvoiceState.Editable ? i.Stamp : 0),
            Total: matches.sum(i => i.Total) + 0.08 * matches.sum(i => i.Total)
          }
        });

      if (this.selected.partner) this.select(this.selected.partner.ID == 0 ? this.all : this.partners.source.first(i => i.partner.ID == this.selected.partner.ID));
    });
  }

  ngOnInit(): void {

    main(() => {
      this.layout.media('footer', 'none');
      this.layout.title = Multilingual.getText("Xuất hàng cho khách");
    });

    this.synchronize();
  }

  public source(tag: string): string {
    let i = this._tags.get(tag);
    if (!i) return '';

    let p = this._partners.get(i.PartnerID);
    return p ? p.Name : '';
  }

  public get editable(): boolean {
    return this.selected.invoice && this.selected.invoice.State == InvoiceState.Editable;
  }

  public get savable(): boolean {
    return this.selected.invoice != null && this.selected.invoice.Details.length > 0 && this.selected.invoice.Details.count(i => i.ActualQuantity <= 0 || i.QuoteUnitPrice <= 0) < 1 && this.edited;
  }

  public get confirmable(): boolean {
    return this.selected.invoice != null && this.selected.invoice.State == InvoiceState.Editable && this.selected.invoice.Details.length > 0 && this.selected.invoice.Details.count(i => i.QuoteQuantity <= 0 || i.QuoteUnitPrice <= 0) < 1 && !this.edited;
  }

  public get cancelable(): boolean {
    return this.selected.invoice != null && this.selected.invoice.State == InvoiceState.Editable && ((this.selected.invoice.Details.length > 0 && !this.edited) || this.selected.invoice.ID == 0 || (this.selected.invoice.Details.length < 1 && this.edited));
  }


  public user(id: number): UserRecord {
    return this._users.get(id);
  }

  public partner(obj: InvoiceRecord | InvoiceGroup): PartnerRecord {

    return obj['partner'] ? obj['partner'] : this._partners.get(obj['PartnerID']);
  }

  public state = invoiceState;

  public group: DetailExtractor<DetailGroup> = {
    count: i => i.records.length,
    actualQuantity: i => i.records.sum(i => i.ActualQuantity),
    quoteQuantity: i => i.records.sum(i => i.QuoteQuantity),
    quoteUnitPrice: i => i.records.length > 0 ? i.records.sum(i => i.QuoteUnitPrice) / i.records.length : 0,
    quoteIcon: i => 'đ',
    subtotal: i => i.records.sum(i => i.QuoteUnitPrice * i.QuoteQuantity) + 0.08 * i.records.sum(i => i.QuoteUnitPrice * i.QuoteQuantity),

    sum: (field: string, unit?: ProductUnit) => {
      switch (field) {
        case 'Count': return this.selected.details.source.sum(i => !unit || unit == i.Unit ? i.records.length : 0);
        case 'ActualQuantity': return this.selected.details.source.sum(i => !unit || unit == i.Unit ? this.group.actualQuantity(i) : 0);
        case 'Reduced': return this.selected.details.source.sum(i => !unit || unit == i.Unit ? this.group.actualQuantity(i) - this.group.quoteQuantity(i) : 0);
        case 'QuoteQuantity': return this.selected.details.source.sum(i => !unit || unit == i.Unit ? this.group.quoteQuantity(i) : 0);
        case 'Subtotal': return this.selected.details.source.sum(i => !unit || unit == i.Unit ? this.group.subtotal(i) : 0);
        default: return null;
      }
    }
  };

  public extractor(field: string): SortableExtractor<any> {
    return {
      extract: i => {
        switch (field) {
          case 'Count': return i.LengthCount + i.WeightCount;
          case 'Partner': return this.partner(i).Name;
          default: return i[field];
        }
      }
    }
  }

  public select(group: InvoiceGroup) {
    this.selected.partner = group ? group.partner : null;
    this.invoices.source = group ? group.invoices : [];
  }

  public open(invoice: InvoiceRecord) {

    if (invoice.ID > 0 && invoice.Details.length == 0) {
      this.sync.with(ExportsComponent).repeat(() => this.ds.invoices('get', { ID: invoice.ID }).pipe(map(invoices => {
        if (invoices && invoices.length == 1) {
          invoice.Details = invoices[0].Details;
          return true;
        }
        return false;
      })));
    }

    if (invoice.State == InvoiceState.Confirmed) {
      this.sync.when(ExportsComponent, PartnersService).commit(() => {
        let modal = this.modal.show(ViewerComponent, { state: { invoice: invoice } });
        modal.content.unconfirmed.subscribe(result => this.synchronize());
      });

      return;
    }

    this.sync.when(ExportsComponent).commit(() => {

      invoice = { ...invoice, Details: invoice.Details.map(i => ({ ...i })) };

      let lu = invoice.Details.lookup(i => i.ID, i => true);
      this.goods.source = this.goodLU.source.filter(i => !lu[i.ID] && i.ExportInvoiceID == 0).order(i => [i.ImportInvoiceTag, i.SKU, i.Color, i.ActualQuantity].join('/'));

      this.selected.partner = this.partner(invoice);
      this.selected.details.source = invoice.Details.group(i => i.ImportInvoiceTag + '/' + i.SKU + '/' + i.Color + '/' + i.Unit + '/' + i.QuoteUnitPrice + '/' + i.IsPriceEditable).map(i => ({ ImportInvoiceTag: i.items[0].ImportInvoiceTag, SKU: i.items[0].SKU, Color: i.items[0].Color, Unit: i.items[0].Unit, Reduced: i.items.sum(x => x.ActualQuantity - x.QuoteQuantity), records: i.items }));

      this.selected.invoice = invoice;
    });
  }

  public close() {

    if (this.edited) {

      this.modal.show(MessageBoxComponent, {
        ignoreBackdropClick: true, state: {
          closable: false,
          message: "Đơn đặt hàng cho " + this.selected.partner.Name + " có các thay đổi chưa lưu lại. Đóng đơn sẽ không lưu lại các thay đổi đó. Bạn có muốn đóng đơn?",
          title: "Xác nhận",
          buttons: [
            {
              icon: 'fa fa-check', class: 'bg-warning text-white', name: 'Đóng đơn', click: e => {

                this.selected.invoice = null;
                this.edited = false;
              }
            },

            { icon: "fa fa-undo", class: 'bg-success text-white', name: 'Quay lại' }
          ]
        }
      });
    }
    else {

      this.selected.invoice = null;
      this.edited = false;
    }
  }

  public confirm() {
    this.modal.show(MessageBoxComponent, {
      ignoreBackdropClick: true, state: {
        closable: false,
        message: "Bạn muốn xác nhận đơn đặt hàng cho " + this.selected.partner.Name + "?",
        title: "Xác nhận",
        buttons: [
          {
            icon: 'fa fa-check', class: 'bg-warning text-white', name: 'Xác nhận đơn', click: e => {

              this.sync.with(ExportsComponent).commit(done => {

                this.ds.invoices('confirm', this.selected.invoice).subscribe(invoice => {

                  if (invoice && invoice.length == 1) {

                    this.close();
                    this.synchronize();
                  }

                  done();
                });
              });

            }
          },

          { icon: "fa fa-undo", class: 'bg-success text-white', name: 'Quay lại' }
        ]
      }
    });
  }

  public cancel() {

    if (this.selected.invoice.ID == 0 && this.selected.invoice.Details.length == 0) {
      this.edited = false;
      this.close();
      return;
    }

    this.modal.show(MessageBoxComponent, {
      ignoreBackdropClick: true, state: {
        closable: false,
        message: "Bạn muốn hủy đơn nhập hàng cho " + this.selected.partner.Name + "?",
        title: "Xác nhận",
        buttons: [
          {
            icon: 'fa fa-times', class: 'bg-danger text-white', name: 'Hủy đơn', click: e => {

              if (this.selected.invoice.ID == 0) {
                this.close();
              }
              else {

                this.sync.with(ExportsComponent).commit(done => {

                  this.ds.invoices('cancel', this.selected.invoice).subscribe(invoice => {
                    if (invoice) {

                      this.edited = false;
                      this.close();
                      this.synchronize();
                    }

                    done();
                  });

                });
              }
            }
          },

          { icon: "fa fa-undo", class: 'bg-success text-white', name: 'Quay lại' }
        ]
      }
    });
  }

}
